/**
 * The caller's clients, from `GET /api/v1/clients/`.
 *
 * The endpoint is paginated - `page` and `page_size`, 25 a page by default and
 * 100 at most - and takes a `search` term, matched case-insensitively as a
 * substring of the first name, last name, phone number or email.
 *
 * It also takes `status`, which is what the list screen's three tabs are:
 * `ACTIVE`, `INACTIVE` and `NEW`, one request each. That reading used to be
 * made in the browser off `archived_at`, which meant the screen could only
 * tell two lists apart and had to hold every client to do it.
 *
 * Paging is still walked rather than served a page at a time - see
 * `fetchAllClients` - because the screen counts each tab and searches within
 * one, and it is now a walk of a third of the roster rather than all of it.
 *
 * On top of the bearer token this needs a completed workspace onboarding; the
 * backend answers 403 without one, which arrives as an `ApiError` carrying its
 * own message.
 */

// `validationMessage` digs the field-level problems out of a
// `validation_failed` 400, whose own message is only ever the generic
// "Validation failed" - see `lib/api/errors`.
import {
  api,
  API_ENDPOINTS,
  ApiError,
  isPaymentRequestCancelled,
  paymentPlanName,
  toAmountNumber,
  toLookupReferences,
  validationMessage,
} from "../../../lib/api";
import type {
  PaginatedResponse,
  PaymentRequestResponse,
} from "../../../lib/api";
import { toNationalNumber } from "../../../lib/fieldFormat";
import type { GetToken } from "../../auth/types/auth.types";
// The state of a raised form, read off the client the same way the payment
// request on it is - see `preconsultationFormsService`, which owns the shape.
import { toPreconsultationFormSummary } from "../../preconsultation/services/preconsultationFormsService";
import type {
  PreconsultationFormAnswers,
  PreconsultationFormRecord,
} from "../../preconsultation/services/preconsultationFormsService";
import { NO_OTHER_DETAILS, statusOf } from "../clientMapping";
import type {
  ClientListStatus,
  ClientOtherDetails,
  ClientPage,
  ClientRecord,
  ClientResponse,
  ClientSubscription,
  CreateClientPayload,
  HeightUnit,
  LookupOption,
  LookupResponse,
  WeightUnit,
} from "../types/clients.types";

/** The largest page the endpoint will serve. Anything above is clamped to it. */
export const MAX_PAGE_SIZE = 100;

/**
 * What every call asks for unless it says otherwise, and what the walk below
 * pages by. Ten, matching the ten rows the list screen shows, so a page of the
 * API is a page of the table.
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Stops a broken `next` from looping forever. At ten a page this is 2,000
 * clients - far past what any one practice has, and the point at which the
 * list screen would need the server to filter by archived rather than a
 * bigger cap here.
 */
const MAX_PAGES = 200;

const toLookup = (
  response: LookupResponse | null | undefined
): LookupOption | null =>
  response ? { id: response.id, name: response.name } : null;

/** Reference lists arrive as arrays; a null or a missing one is no entries. */
const toLookupList = (
  response: LookupResponse[] | null | undefined
): LookupOption[] =>
  (response ?? [])
    .filter((row) => row && typeof row.name === "string")
    .map((row) => ({ id: row.id, name: row.name }));

/**
 * What the client bought, out of the request that sold it.
 *
 * A request that was called off gives nothing: the client is not on that
 * plan, and a cancelled sale drawn beside their goal and their allergies
 * would read as one they hold. One still awaiting payment does show — the
 * plan was agreed, and the Payments tab is where its state is answered for.
 *
 * Every field is defended: a request the backend sent with a plan it could
 * not name is still a card the tab has to draw.
 */
const toSubscription = (
  response: PaymentRequestResponse | null | undefined
): ClientSubscription | undefined => {
  if (!response || isPaymentRequestCancelled(response)) return undefined;

  return {
    planName: paymentPlanName(response),
    duration: response.duration_label ?? "",
    amount: toAmountNumber(response.amount_due),
    // `paid_on` is the day; the receipt carries it too, and is what a request
    // settled without the top-level field being set still reads from. Neither
    // is there while it is unpaid, so the day it was raised stands in.
    purchasedOn:
      response.paid_on ?? response.payment?.received_on ?? response.generated_on,
  };
};

/** A measurement as the form holds one: text, and empty where there is none. */
const toMeasure = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  // A backend that stores these as decimals sends `168.00`, which is not what
  // anybody typed and not what the field should reopen on.
  return text.replace(/\.0+$/, "");
};

/** The unit beside a measurement, or the app's own default where none came. */
const toUnit = <Unit extends string>(
  value: string | null | undefined,
  units: readonly Unit[],
  fallback: Unit
): Unit => {
  const held = (value ?? "").trim().toLowerCase();
  return units.find((unit) => unit === held) ?? fallback;
};

const HEIGHT_UNITS: readonly HeightUnit[] = ["cm", "in"];
const WEIGHT_UNITS: readonly WeightUnit[] = ["kg", "lb"];

/** A list of free text — the dishes, the medications. Blanks dropped. */
const toTextList = (value: string[] | null | undefined): string[] =>
  (value ?? [])
    .map((entry) => (entry ?? "").trim())
    .filter(Boolean);

/** `HH:MM` out of whatever the backend keeps a time of day as. */
const toTime = (value: string | null | undefined): string => {
  const match = /^(\d{1,2}):(\d{2})/.exec((value ?? "").trim());
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

/**
 * The optional half of a client's record.
 *
 * Every field defended and every one allowed to be missing: these arrived long
 * after the record itself did, and a backend that has not grown them yet must
 * still answer with a client the screens can draw. What is not there is empty
 * rather than absent, so nothing above has to test before it reads.
 */
const toOtherDetails = (response: ClientResponse): ClientOtherDetails => ({
  height: toMeasure(response.height_value),
  heightUnit: toUnit(
    response.height_unit,
    HEIGHT_UNITS,
    NO_OTHER_DETAILS.heightUnit
  ),
  weight: toMeasure(response.weight_value),
  weightUnit: toUnit(
    response.weight_unit,
    WEIGHT_UNITS,
    NO_OTHER_DETAILS.weightUnit
  ),
  targetWeight: toMeasure(response.target_weight_value),
  targetWeightUnit: toUnit(
    response.target_weight_unit,
    WEIGHT_UNITS,
    NO_OTHER_DETAILS.targetWeightUnit
  ),
  // The list, and behind it the single field it replaced — a record written
  // before the question took several holds its one cuisine there.
  cuisines: toLookupList(
    response.favorite_cuisines ??
      (response.favorite_cuisine ? [response.favorite_cuisine] : null)
  ).map((row) => row.name),
  dishesLoved: toTextList(response.favorite_dishes),
  dishesAvoided: toTextList(response.foods_to_avoid),
  // Catalog rows now, so what a screen shows is the names off them.
  conditions: toLookupList(response.medical_conditions).map((row) => row.name),
  // `null` rather than `false` where the question was never put: a client
  // nobody asked is not a client who said no.
  onMedication:
    typeof response.takes_medications === "boolean"
      ? response.takes_medications
      : null,
  medications: toTextList(response.medications),
  workStart: toTime(response.working_hours_start),
  workEnd: toTime(response.working_hours_end),
  noFixedWorkHours: !!response.has_no_fixed_working_hours,
  sleepStart: toTime(response.sleep_start),
  sleepEnd: toTime(response.sleep_end),
  noFixedSleep: !!response.has_no_fixed_sleep_schedule,
  travel: (response.travel_frequency ?? "").trim(),
  alcohol: (response.alcohol_frequency ?? "").trim(),
  smoking: (response.smoking_frequency ?? "").trim(),
});

/**
 * The same answers as a raised form carries them, for the responses that send
 * them that way round.
 *
 * The create and update endpoints answer with the form they raised rather than
 * with a copy of its answers at the top level, so a client saved with the
 * accordion filled in would otherwise come back with the whole optional half
 * blank — and the screen that opens on it would show a client their own
 * dietitian had just described as having nothing on record.
 *
 * `toPreconsultationFormSummary` has already done the reading; the units and
 * the times only have to be narrowed to what a client's record types them as.
 */
const fromFormAnswers = (
  answers: PreconsultationFormAnswers | undefined
): ClientOtherDetails => {
  if (!answers) return NO_OTHER_DETAILS;

  return {
    height: answers.height,
    heightUnit: toUnit(
      answers.heightUnit,
      HEIGHT_UNITS,
      NO_OTHER_DETAILS.heightUnit
    ),
    weight: answers.weight,
    weightUnit: toUnit(
      answers.weightUnit,
      WEIGHT_UNITS,
      NO_OTHER_DETAILS.weightUnit
    ),
    targetWeight: answers.targetWeight,
    targetWeightUnit: toUnit(
      answers.targetWeightUnit,
      WEIGHT_UNITS,
      NO_OTHER_DETAILS.targetWeightUnit
    ),
    cuisines: answers.cuisines,
    dishesLoved: answers.dishesLoved,
    dishesAvoided: answers.dishesAvoided,
    conditions: answers.conditions,
    onMedication: answers.takesMedications,
    medications: answers.medications,
    workStart: answers.workStart,
    workEnd: answers.workEnd,
    noFixedWorkHours: answers.noFixedWorkHours,
    sleepStart: answers.sleepStart,
    sleepEnd: answers.sleepEnd,
    noFixedSleep: answers.noFixedSleep,
    travel: answers.travel,
    alcohol: answers.alcohol,
    smoking: answers.smoking,
  };
};

/**
 * Whether the response carried the optional half at the top level at all.
 *
 * Presence, not content: the list and detail endpoints send every one of these
 * keys and send them `null` for a client who has answered none, which is a
 * different thing from the create and update responses leaving them off
 * altogether. Only the second should fall through to the raised form.
 */
const carriesOtherDetails = (response: ClientResponse): boolean =>
  (
    [
      "height_value",
      "weight_value",
      "favorite_cuisines",
      "favorite_cuisine",
      "favorite_dishes",
      "foods_to_avoid",
      "medical_conditions",
      "takes_medications",
      "medications",
      "working_hours_start",
      "sleep_start",
      "travel_frequency",
      "alcohol_frequency",
      "smoking_frequency",
    ] as const
  ).some((field) => response[field] !== undefined);

/**
 * Every pre-consultation form on the response, however it sent them: the list
 * on a read, and the single `pre_consultation_form` the create and update
 * answer with. A client carries one or the other, so the list wins where both
 * somehow arrive.
 */
const formsOf = (response: ClientResponse): PreconsultationFormRecord[] => {
  const raised = response.pre_consultation_forms ?? [];
  if (raised.length > 0) return raised;
  return response.pre_consultation_form ? [response.pre_consultation_form] : [];
};

const toRecord = (response: ClientResponse): ClientRecord => {
  const forms = formsOf(response).map((form) => toPreconsultationFormSummary(form));

  return {
    publicId: response.public_id,
    title: toLookup(response.title),
    firstName: response.first_name ?? "",
    lastName: response.last_name ?? "",
    gender: response.gender ?? "",
    dateOfBirth: response.date_of_birth,
    profession: response.profession ?? "",
    lifestyle: toLookup(response.life_style),
    fullAddress: response.full_address ?? "",
    // `place` is the state - see the note on `ClientResponse`.
    state: response.place ?? "",
    postalCode: response.postal_code ?? "",
    city: response.city ?? "",
    phone: toNationalNumber(response.phone_number),
    whatsapp: toNationalNumber(response.whatsapp_number),
    email: response.email ?? "",
    // `goals` now, `goal` on a record the backend has not migrated, and either
    // one object or a list of them — all read as the same list. See
    // `toLookupReferences`, which is where that tolerance is spelled out.
    // `toLookupReferences` already drops the nameless rows and reads the id
    // from whichever key carried it, so there is no `toLookupList` here: a
    // client's goals are services now and arrive under `public_id`.
    goals: toLookupReferences(response.goals ?? response.goal),
    dietaryPattern: toLookup(response.dietary_pattern),
    foodAllergies: toLookupList(response.food_allergies),
    archivedAt: response.archived_at,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    // In the order the backend listed them, which is newest first. Reordering is
    // `governingForm`'s business, not this one's - a mapping that sorted would be
    // deciding something on the screen's behalf.
    preconsultationForms: forms,
    engagementDuration: response.current_engagement_duration ?? null,
    // The backend's own reading of where the engagement stands. `archived_at` is
    // passed with it only as the fallback for a response that carried no status
    // at all - the create and update endpoints answer before there is one.
    status: statusOf(response.status, response.archived_at),
    subscription: toSubscription(response.payment_request),
    // Off the response itself where it carried the answers, and off the form it
    // raised where it did not - see `carriesOtherDetails`.
    other: carriesOtherDetails(response)
      ? toOtherDetails(response)
      : fromFormAnswers(forms[0]?.answers),
  };
};

export interface FetchClientsOptions {
  /** 1-based, the way the endpoint counts. */
  page?: number;
  /**
   * Defaults to `DEFAULT_PAGE_SIZE`, and is capped at `MAX_PAGE_SIZE` - all
   * the backend will serve.
   */
  pageSize?: number;
  /**
   * Matched case-insensitively against the first name, last name, phone
   * number and email. Blank or whitespace is left off rather than sent, so an
   * empty search box asks for the unfiltered list.
   */
  search?: string;
  /**
   * Which of the three engagement states to serve. Left off, every client
   * comes back whatever state they are in - which is what the screens that
   * pick a client out of the whole roster want.
   */
  status?: ClientListStatus;
  signal?: AbortSignal;
}

/** One page of clients. */
export async function fetchClientsPage(
  getToken: GetToken,
  {
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    search,
    status,
    signal,
  }: FetchClientsOptions = {}
): Promise<ClientPage> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load clients.");
  }

  const response = await api.get<PaginatedResponse<ClientResponse>>(
    API_ENDPOINTS.clients.list,
    {
      token,
      // `page`, `search` and `status` are all optional - left out, the backend
      // serves the first page of every client. `undefined` entries are dropped
      // by the API client, so an empty search never reaches the URL and
      // neither does an unfiltered call's absent status.
      query: {
        page,
        page_size: Math.min(pageSize, MAX_PAGE_SIZE),
        search: search?.trim() || undefined,
        status,
      },
      signal,
    }
  );

  return {
    count: response?.count ?? 0,
    hasNext: !!response?.next,
    clients: (response?.results ?? []).map(toRecord),
  };
}

/**
 * One client, by the `public_id` a list row carries.
 *
 * The response is a single client in exactly the shape the list returns them -
 * the same reference objects, the same `place`-is-the-state field - so both go
 * through `toRecord` rather than each keeping its own copy of the mapping.
 *
 * A `public_id` that is not the caller's, or not a client at all, comes back
 * 404 as `{ code: "client_not_found", message: "Client not found." }`. That
 * reaches the caller as an `ApiError` carrying the message, the 404 status,
 * and the body itself on `data` - rather than as an empty record. `useClient`
 * branches on the status to tell a missing client from a failed request.
 */
export async function fetchClient(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<ClientRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load a client.");
  }

  const response = await api.get<ClientResponse>(
    API_ENDPOINTS.clients.detail(publicId),
    { token, signal }
  );

  return toRecord(response);
}

/**
 * A built payload as the two write endpoints take it: `multipart/form-data`.
 *
 * Three rules, and they are the whole of the difference between the object
 * `clientPayload` builds and what goes on the wire:
 *
 *   - a list is a repeated key, `food_allergies=14&food_allergies=9`, since a
 *     multipart field holds one value;
 *   - `pre_consultation` is one JSON string, which is how the endpoint takes
 *     a block of answers through a body that has no nesting of its own;
 *   - anything absent stays absent. A field left off is a field not touched,
 *     which is what makes the edit path safe to send a whole form through.
 *
 * The last rule has a cost worth naming: an empty list cannot be expressed.
 * `food_allergies: []` leaves the key off, and on the `PATCH` an absent key
 * means unchanged - so taking every allergy off a client no longer clears
 * them. Nothing here can fix that; the endpoint needs a way to say "none".
 */
const toFormBody = (payload: CreateClientPayload): FormData => {
  const body = new FormData();

  Object.entries(payload).forEach(([field, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((entry) => body.append(field, String(entry)));
      return;
    }

    // `pre_consultation`, and nothing else: the only field whose value is a
    // shape rather than a scalar.
    if (typeof value === "object") {
      body.append(field, JSON.stringify(value));
      return;
    }

    body.append(field, String(value));
  });

  return body;
};

/**
 * Creates a client and returns the record the backend saved.
 *
 * The reference fields go up as ids - see `toCreateClientPayload`, which is
 * what turns the names the form displays into them - and come back as whole
 * `{ id, name }` objects, so the response maps like any other client. The
 * optional details go up in the `pre_consultation` block and come back as the
 * approved form the backend raises out of it, which `toRecord` reads into the
 * same place a read would have found them.
 *
 * `multipart/form-data` - see `toFormBody`. A 400 arrives as an `ApiError`
 * whose message names the fields the backend rejected rather than just
 * "Validation failed"; a 404 means one of the ids was not in its list, which
 * is a catalog that moved under an open form.
 */
export async function createClient(
  getToken: GetToken,
  payload: CreateClientPayload,
  signal?: AbortSignal
): Promise<ClientRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot add a client.");
  }

  try {
    const response = await api.post<ClientResponse>(
      API_ENDPOINTS.clients.create,
      toFormBody(payload),
      { token, signal }
    );

    return toRecord(response);
  } catch (error) {
    if (error instanceof ApiError) {
      const detailed = validationMessage(error);
      if (detailed) {
        throw new ApiError(detailed, error.status, error.data);
      }
    }
    throw error;
  }
}

/**
 * Edits a client and returns the record as the backend now holds it - so the
 * screen that opens next reads what was stored rather than what was typed.
 *
 * `multipart/form-data`, the same as the create above and through the same
 * `toFormBody`: `goals` and `food_allergies` are a repeated key each, and the
 * optional details are the one `pre_consultation` string.
 *
 * The endpoint takes a partial update, but the form collects every field, so
 * every field goes up whether or not it was touched. `goals` and
 * `food_allergies` are replace-all: what is sent becomes the client's entire
 * list. The one thing that cannot be said is an empty one - see `toFormBody`.
 *
 * A 400 arrives as an `ApiError` naming the fields the backend rejected rather
 * than just "Validation failed"; a 404 is `client_not_found`.
 */
export async function updateClient(
  getToken: GetToken,
  publicId: string,
  payload: CreateClientPayload,
  signal?: AbortSignal
): Promise<ClientRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot update a client.");
  }

  try {
    const response = await api.patch<ClientResponse>(
      API_ENDPOINTS.clients.update(publicId),
      toFormBody(payload),
      { token, signal }
    );

    return toRecord(response);
  } catch (error) {
    if (error instanceof ApiError) {
      const detailed = validationMessage(error);
      if (detailed) {
        throw new ApiError(detailed, error.status, error.data);
      }
    }
    throw error;
  }
}

// Raising a client's pre-consultation form is not here: the same endpoint
// raises the same form for an enquiry, and only the id differs. See
// `preconsultation/plans/preconsultationFormsService`.

/**
 * Every client the caller has, walked a page at a time. `search` and `status`
 * are passed through, so the walk covers the matches in one list rather than
 * the whole roster.
 *
 * Sequential rather than parallel: the first response is what says whether
 * there is a second page at all, and a practice large enough for the request
 * count to matter is one that needs the list screen to page against the server
 * rather than in the browser.
 */
export async function fetchAllClients(
  getToken: GetToken,
  {
    search,
    status,
    signal,
  }: Pick<FetchClientsOptions, "search" | "status" | "signal"> = {}
): Promise<ClientRecord[]> {
  const clients: ClientRecord[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await fetchClientsPage(getToken, {
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      search,
      status,
      signal,
    });

    clients.push(...result.clients);

    // An empty page as well as `next: null` ends the walk - a page that says
    // there is another but returns nothing would otherwise spin to the cap.
    if (!result.hasNext || result.clients.length === 0) break;
  }

  return clients;
}
